import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { CalendarRange } from 'lucide-react'
import { useState } from 'react'
import type { DateRange as DayPickerDateRange } from 'react-day-picker'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
	Drawer,
	DrawerContent,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from '@/components/ui/drawer'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { useDeviceType } from '@/hooks/useDeviceType'
import { cn } from '@/lib/utils'
import type { DateRange } from '@/types'

export type PeriodPreset =
	| 'CURRENT_MONTH'
	| 'PREVIOUS_MONTH'
	| 'LAST_3_MONTHS'
	| 'YEAR'
	| 'CUSTOM'

export interface PeriodFilterValue {
	preset: PeriodPreset
	range: DateRange
}

const PRESET_LABELS: Record<PeriodPreset, string> = {
	CURRENT_MONTH: 'Mês atual',
	PREVIOUS_MONTH: 'Mês passado',
	LAST_3_MONTHS: 'Últimos 3 meses',
	YEAR: 'Ano',
	CUSTOM: 'Personalizado',
}

const PRESET_ORDER: PeriodPreset[] = [
	'CURRENT_MONTH',
	'PREVIOUS_MONTH',
	'LAST_3_MONTHS',
	'YEAR',
	'CUSTOM',
]

function rangeForPreset(preset: PeriodPreset, custom?: DateRange): DateRange {
	const now = new Date()

	switch (preset) {
		case 'CURRENT_MONTH':
			return {
				start: new Date(now.getFullYear(), now.getMonth(), 1),
				end: new Date(now.getFullYear(), now.getMonth() + 1, 0),
			}
		case 'PREVIOUS_MONTH':
			return {
				start: new Date(now.getFullYear(), now.getMonth() - 1, 1),
				end: new Date(now.getFullYear(), now.getMonth(), 0),
			}
		case 'LAST_3_MONTHS':
			return {
				start: new Date(now.getFullYear(), now.getMonth() - 2, 1),
				end: new Date(now.getFullYear(), now.getMonth() + 1, 0),
			}
		case 'YEAR':
			return {
				start: new Date(now.getFullYear(), 0, 1),
				end: new Date(now.getFullYear(), 11, 31),
			}
		case 'CUSTOM':
			return custom ?? rangeForPreset('CURRENT_MONTH')
	}
}

export function defaultPeriodFilterValue(): PeriodFilterValue {
	return { preset: 'CURRENT_MONTH', range: rangeForPreset('CURRENT_MONTH') }
}

interface CommitmentMatrixPeriodFilterProps {
	value: PeriodFilterValue
	onChange: (value: PeriodFilterValue) => void
}

export function CommitmentMatrixPeriodFilter({
	value,
	onChange,
}: CommitmentMatrixPeriodFilterProps) {
	const { isMobile } = useDeviceType()
	const [desktopCalendarOpen, setDesktopCalendarOpen] = useState(false)
	const [mobileCalendarOpen, setMobileCalendarOpen] = useState(false)
	const [draftRange, setDraftRange] = useState<DayPickerDateRange | undefined>({
		from: value.range.start,
		to: value.range.end,
	})

	const handleMobilePresetChange = (preset: PeriodPreset) => {
		if (preset === 'CUSTOM') {
			setMobileCalendarOpen(true)
			return
		}
		onChange({ preset, range: rangeForPreset(preset) })
	}

	const handleDesktopPresetClick = (preset: PeriodPreset) => {
		onChange({ preset, range: rangeForPreset(preset) })
	}

	const mobileLabel =
		value.preset === 'CUSTOM'
			? `Personalizado: ${format(value.range.start, 'dd/MM/yy')} – ${format(value.range.end, 'dd/MM/yy')}`
			: PRESET_LABELS[value.preset]

	const customTriggerLabel =
		value.preset === 'CUSTOM'
			? `${format(value.range.start, 'dd/MM/yy')} – ${format(value.range.end, 'dd/MM/yy')}`
			: 'Personalizado'

	const handleApplyCustomRange = () => {
		if (!draftRange?.from) return
		const range: DateRange = {
			start: draftRange.from,
			end: draftRange.to ?? draftRange.from,
		}
		onChange({ preset: 'CUSTOM', range })
		setDesktopCalendarOpen(false)
		setMobileCalendarOpen(false)
	}

	return (
		<div className="flex flex-col gap-2">
			{isMobile ? (
				<Select
					value={value.preset}
					onValueChange={(preset) =>
						handleMobilePresetChange(preset as PeriodPreset)
					}
				>
					<SelectTrigger>
						<SelectValue>{mobileLabel}</SelectValue>
					</SelectTrigger>
					<SelectContent>
						{PRESET_ORDER.map((preset) => (
							<SelectItem key={preset} value={preset}>
								{PRESET_LABELS[preset]}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			) : (
				<div className="flex flex-wrap items-center gap-2">
					{PRESET_ORDER.filter((p) => p !== 'CUSTOM').map((preset) => (
						<Button
							key={preset}
							type="button"
							size="sm"
							variant={value.preset === preset ? 'default' : 'outline'}
							onClick={() => handleDesktopPresetClick(preset)}
							className={cn(
								value.preset === preset &&
									'bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90',
							)}
						>
							{PRESET_LABELS[preset]}
						</Button>
					))}

					<Popover
						open={desktopCalendarOpen}
						onOpenChange={setDesktopCalendarOpen}
					>
						<PopoverTrigger asChild>
							<Button
								type="button"
								size="sm"
								variant={value.preset === 'CUSTOM' ? 'default' : 'outline'}
								className={cn(
									'gap-1.5',
									value.preset === 'CUSTOM' &&
										'bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90',
								)}
							>
								<CalendarRange className="size-3.5" />
								{customTriggerLabel}
							</Button>
						</PopoverTrigger>
						<PopoverContent className="w-auto p-0" align="start">
							<Calendar
								mode="range"
								selected={draftRange}
								onSelect={setDraftRange}
								locale={ptBR}
								numberOfMonths={2}
								disabled={{ after: new Date() }}
							/>
							<div className="flex justify-end gap-2 border-t border-kaefer-light-silver/60 p-3">
								<Button
									type="button"
									size="sm"
									variant="cancel"
									onClick={() => setDesktopCalendarOpen(false)}
								>
									Cancelar
								</Button>
								<Button
									type="button"
									size="sm"
									disabled={!draftRange?.from}
									onClick={handleApplyCustomRange}
									className="bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90"
								>
									Aplicar
								</Button>
							</div>
						</PopoverContent>
					</Popover>
				</div>
			)}

			{isMobile && (
				<Drawer open={mobileCalendarOpen} onOpenChange={setMobileCalendarOpen}>
					<DrawerContent>
						<DrawerHeader>
							<DrawerTitle>Selecione o período</DrawerTitle>
						</DrawerHeader>
						<div className="flex justify-center overflow-x-auto">
							<Calendar
								mode="range"
								selected={draftRange}
								onSelect={setDraftRange}
								locale={ptBR}
								numberOfMonths={1}
								disabled={{ after: new Date() }}
								className="w-fit"
							/>
						</div>
						<DrawerFooter className="flex-row justify-end">
							<Button
								type="button"
								size="sm"
								variant="cancel"
								onClick={() => setMobileCalendarOpen(false)}
							>
								Cancelar
							</Button>
							<Button
								type="button"
								size="sm"
								disabled={!draftRange?.from}
								onClick={handleApplyCustomRange}
								className="bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90"
							>
								Aplicar
							</Button>
						</DrawerFooter>
					</DrawerContent>
				</Drawer>
			)}
		</div>
	)
}
